import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../hooks/useAuth';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const { accessToken: token } = useAuth();

  const fetchUsers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);
      if (roleFilter) params.append('role', roleFilter);

      const res = await fetch(`${API_URL}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setUsers(data.users || []);
      setPagination(data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [token, search, roleFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const changeRole = async (userId, newRole) => {
    try {
      await fetch(`${API_URL}/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });
      fetchUsers(pagination.page);
    } catch (err) {
      console.error(err);
    }
  };

  const toggleBan = async (user) => {
    const action = user.banned_at ? 'unban' : 'ban';
    if (action === 'ban') {
      const reason = prompt('Raison du bannissement:');
      if (!reason) return;

      await fetch(`${API_URL}/admin/users/${user.id}/ban`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ban_reason: reason })
      });
    } else {
      await fetch(`${API_URL}/admin/users/${user.id}/unban`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
    }
    fetchUsers(pagination.page);
  };

  return (
    <div>
      <div className="admin-page-header">
        <h2>Gestion des utilisateurs</h2>
        <p>{pagination.total} utilisateur{pagination.total > 1 ? 's' : ''} inscrits</p>
      </div>

      <div className="admin-toolbar">
        <input
          className="admin-search"
          placeholder="Rechercher par nom ou email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
        />
        <select
          className="admin-filter"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="">Tous les roles</option>
          <option value="basic">Basic</option>
          <option value="verified">Verifie</option>
          <option value="agent">Agent</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      <div className="glass-card admin-table-container">
        {loading ? (
          <div style={{padding: '2rem', textAlign: 'center', color: 'var(--text-light)'}}>
            Chargement...
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nom</th>
                <th>Email</th>
                <th>Tel</th>
                <th>Role</th>
                <th>Trust</th>
                <th>Annonces</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={u.banned_at ? {opacity: 0.6} : {}}>
                  <td>#{u.id}</td>
                  <td>{u.nom} {u.prenom}</td>
                  <td style={{fontSize: '0.8rem'}}>{u.email}</td>
                  <td>
                    {u.telephone || '—'}
                    {u.phone_verified && ' ✅'}
                  </td>
                  <td>
                    <select
                      className="role-select"
                      value={u.role}
                      onChange={(e) => changeRole(u.id, e.target.value)}
                    >
                      <option value="basic">basic</option>
                      <option value="verified">verified</option>
                      <option value="agent">agent</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td>
                    <span style={{
                      color: u.trust_score >= 80 ? '#1D9E75' :
                             u.trust_score >= 50 ? '#BA7517' :
                             u.trust_score >= 20 ? '#85563A' : '#999'
                    }}>
                      {u.trust_score}
                    </span>
                  </td>
                  <td>{u.nb_annonces}</td>
                  <td>
                    {u.banned_at ? (
                      <span className="badge badge-banned" title={u.ban_reason}>Banni</span>
                    ) : (
                      <span className="badge badge-active">Actif</span>
                    )}
                  </td>
                  <td>
                    <div className="admin-actions">
                      <button
                        className={`btn-action ${u.banned_at ? 'approve' : 'reject'}`}
                        onClick={() => toggleBan(u)}
                      >
                        {u.banned_at ? 'Debannir' : 'Bannir'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan="9" style={{textAlign: 'center', padding: '2rem', color: 'var(--text-light)'}}>
                    Aucun utilisateur trouve
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {pagination.pages > 1 && (
        <div className="admin-pagination">
          <button
            disabled={pagination.page <= 1}
            onClick={() => fetchUsers(pagination.page - 1)}
          >
            ← Precedent
          </button>
          <span>Page {pagination.page} / {pagination.pages}</span>
          <button
            disabled={pagination.page >= pagination.pages}
            onClick={() => fetchUsers(pagination.page + 1)}
          >
            Suivant →
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
